import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import { AlertService } from '@gv/ammo-grome';
import { AlertType, FunctionAuthorization, GardianService, IFunctionAuthorization, QueryService } from '@gv/ammo-astra';
import BusinessAnalyticsService from '@/core/spe/dashboard/business-analytics.service';
import BackendI18nService from '@/shared/config/spe/backend-i18n.service';
import Formatter from '@/move-into-ammo/fromatter';
import ParameterService from '@/shared/config/spe/parameter.service';
import ParameterGroup from '@/core/spe/parameter/parameter-group.vue';
import { IParameters, Parameters } from '@/shared/config/spe/parameters';

const ERP_FUNCTION = 'CONSCSI';

@Component({
  components: {
    ParameterGroup,
  },
})
export default class SalesInvoicesDashboard extends Vue {
  @Inject('alertService')
  private alertService: () => AlertService;

  @Inject('queryService')
  private queryService: () => QueryService;

  @Inject('gardianService')
  private gardianService: () => GardianService;

  @Inject('businessAnalyticsService')
  private businessAnalyticsService: () => BusinessAnalyticsService;

  @Inject('backendI18nService')
  private backendI18nService: () => BackendI18nService;

  @Inject('parameterService')
  private parameterService: () => ParameterService;

  @Prop({ required: true, type: Number }) readonly carouselItemIndex;
  @Prop({ required: true, type: Number }) readonly activeCarouselItemIndex;

  public slide: { [k: string]: any } = {
    name: '', // Will be set in created()
    color: '#df1e5a',
    data: {
      currencyCode: '',
      netPriceExcludeTax: 0,
      number: 0,
    },
  };
  public formatter = null;
  public parameters: { [k: string]: any } = {};
  public queryParameters: { [k: string]: any } = null;
  public parametersReady = false;
  public authorized = true;
  public isParameterDrawerActive = true;

  // --- Function authorization ---
  public functionAuthorization: IFunctionAuthorization = new FunctionAuthorization(null, false, false, false, null, [], [], []);
  public isFunctionAuthorizationFetching = false;
  public parameter: IParameters = new Parameters(null, null, null, null, null);

  public salesInvoiceLinesLastThreeYearsMonthTotalsChart: { [k: string]: any } = {
    loading: false,
    chartWidth: '100%',
    options: {},
    series: [],
    originalData: null, // Store original response with i18n keys
    chartKey: 0, // Force re-render when data changes
  };
  public panel: [0];
  public salesInvoiceLinesYearMonthAndMonthsBeforeHomologous: { [k: string]: any } = {
    loading: false,
    chartWidth: '100%',
    options: {},
    series: [],
    originalData: null, // Store original response with i18n keys
    chartKey: 0, // Force re-render when data changes
  };
  public salesRepsSituationChart: { [k: string]: any } = {
    loading: false,
    chartWidth: '100%',
    options: {},
    series: [],
    originalData: null, // Store original response with i18n keys
    chartKey: 0, // Force re-render when data changes
  };
  public tableCustomersRetentionsSearch = '';
  public tableCustomersRetentions: { [k: string]: any } = {
    loading: false,
    headers: [],
    data: [],
    originalHeaders: null, // Store original headers with i18n keys for re-translation
  };

  public metrics = {
    totalBilled: 0,
    totalBilledChange: 0,
    totalCredited: 0,
    totalCreditedChange: 0,
    averageInvoiceValue: 0,
    averageInvoiceValueChange: 0,
    netRevenue: 0,
    annualGrowth: 0,
    annualGrowthChange: 0,
  };

  public tit;

  get disabled(): boolean {
    return this.carouselItemIndex !== this.activeCarouselItemIndex;
  }

  created() {
    // Set i18n title
    this.slide.name = this.$t('rafaleApp.spe.dashboard.salesInvoices.title') as string;

    this.formatter = new Formatter(this.$vuetify.lang.current, this.$vuetify.theme);
    this.retrieveFunctionAuthorization()
      .then(() => {
        const parameters = this.parameterService().fromFunctionAuthorization(this.functionAuthorization);
        this.parameterService().hydrateParameters(this, parameters);
        this.parametersReady = true;
      })
      .catch(err => {
        this.parametersReady = false;
      });
  }

  mounted() {
    this.fetch();
  }

  public beforeDestroy() {
    this.isParameterDrawerActive = false;
  }

  @Watch('activeCarouselItemIndex')
  onActiveCarouselItemIndexChange(val: number) {
    if (val === this.carouselItemIndex) {
      this.fetch();
    }
  }

  @Watch('queryParameters')
  function(newQueryParameters) {
    this.fetch();
  }

  @Watch('$store.state.translationStore.currentLanguage')
  onLanguageChange(newLanguage: string, oldLanguage: string) {
    if (newLanguage !== oldLanguage) {
      // Re-translate existing data instead of re-fetching
      this.retranslateCharts();
    }
  }

  retranslateCharts() {
    console.log('Retranslating invoice charts for language:', this.$i18n.locale);
    // Re-translate all charts that have original data
    if (this.salesInvoiceLinesLastThreeYearsMonthTotalsChart.originalData) {
      this.applyTranslation(
        this.salesInvoiceLinesLastThreeYearsMonthTotalsChart.originalData,
        this.salesInvoiceLinesLastThreeYearsMonthTotalsChart
      );
    }
    if (this.salesInvoiceLinesYearMonthAndMonthsBeforeHomologous.originalData) {
      this.applyTranslation(
        this.salesInvoiceLinesYearMonthAndMonthsBeforeHomologous.originalData,
        this.salesInvoiceLinesYearMonthAndMonthsBeforeHomologous
      );
    }
    if (this.salesRepsSituationChart.originalData) {
      this.applyTranslation(this.salesRepsSituationChart.originalData, this.salesRepsSituationChart);
    }
    // Re-translate table headers
    if (this.tableCustomersRetentions.originalHeaders) {
      this.tableCustomersRetentions.headers = this.backendI18nService().translateTableHeaders(
        JSON.parse(JSON.stringify(this.tableCustomersRetentions.originalHeaders))
      );
    }
  }

  applyTranslation(originalData: any, chart: any) {
    // Translate i18n keys from backend response
    const translatedRes = this.backendI18nService().translateChartData(JSON.parse(JSON.stringify(originalData)));

    console.log('Invoice - Original title:', originalData.options?.title?.text);
    console.log('Invoice - Translated title:', translatedRes.options?.title?.text);

    // Use Vue.set to ensure reactivity
    this.$set(chart, 'options', translatedRes.options);
    this.$set(chart, 'series', translatedRes.series ?? translatedRes.doubleSeries);

    if (chart.options.chart.id === 'sales-orders-lines-totals-by-week') {
      this.tit = chart.options.title.text;
    }

    // Hide ApexCharts built-in title and subtitle (we use v-card-title/subtitle instead for Vuetify consistency)
    // Keep the text for v-card-title/subtitle to access, but don't display it in the chart
    if (chart.options.title) {
      chart.options.title.style = {
        fontSize: '0px',
        color: 'transparent',
      };
      chart.options.title.margin = 0;
    }

    if (chart.options.subtitle) {
      chart.options.subtitle.style = {
        fontSize: '0px',
        color: 'transparent',
      };
      chart.options.subtitle.margin = 0;
    }

    // Colors are now set globally in main.ts via window.Apex.colors

    this.evalFormatter(chart);
  }

  public optionsBar = {
    chart: {
      type: 'bar',
      // height: 250,
      // width: '100%',
      // stacked: true,
      foreColor: '#999',
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: false,
        },
        columnWidth: '75%',
        borderRadius: 7,
      },
    },
    // series: [{
    //   name: "Sessions",
    //   data: [20, 16, 24, 28, 26, 22, 15, 5, 14, 16, 22, 29, 24, 19, 15, 10, 11, 15, 19, 23],
    // }, {
    //   name: "Views",
    //   data: [20, 16, 24, 28, 26, 22, 15, 5, 14, 16, 22, 29, 24, 19, 15, 10, 11, 15, 19, 23],
    // }],
    // labels: [15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 1, 2, 3, 4],
    xaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        show: false,
      },
      labels: {
        show: false,
        style: {
          fontSize: '14px',
        },
      },
    },
    grid: {
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    yaxis: {
      axisBorder: {
        show: false,
      },
      labels: {
        show: false,
      },
    },
    // legend: {
    //   floating: true,
    //   position: 'top',
    //   horizontalAlign: 'right',
    //   offsetY: -36
    // },
    // title: {
    //   text: 'Web Statistics',
    //   align: 'left',
    // },
    // subtitle: {
    //   text: 'Sessions and Views'
    // },
    tooltip: {
      shared: true,
      intersect: false,
    },
  };

  getApexChart(url: string, params: { [k: string]: any }, chart) {
    chart.loading = true;

    this.businessAnalyticsService()
      .retrieve(url, params)
      .then(res => {
        // Store original response with i18n keys
        chart.originalData = res;

        // Apply translation
        this.applyTranslation(res, chart);

        chart.chartKey = (chart.chartKey || 0) + 1;
      })
      .catch(err => {
        chart.series = [];
        this.alertService().showHttpError(this, err.response);
      })
      .finally(() => (chart.loading = false));
  }

  getTable(url: string, params: { [k: string]: any }, table: { [k: string]: any }) {
    table.loading = true;

    this.businessAnalyticsService()
      .retrieve(url, params)
      .then(res => {
        // Store original headers with i18n keys for re-translation
        table.originalHeaders = res.headers;

        // Translate i18n keys in headers if they exist
        table.headers = res.headers ? this.backendI18nService().translateTableHeaders(res.headers) : res.headers;
        table.data = res.data;

        if (res.data.dense) {
          table['dense'] = res.dense;
        }
        if (res.data.class) {
          table['class'] = res.class;
        }
      })
      .catch(err => {
        this.alertService().showHttpError(this, err.response);
      })
      .finally(() => (table.loading = false));
  }
  /**
   * Helper to simplify setting formatter in apex charts.
   * @param chart
   */
  evalFormatter(chart) {
    if (chart.options.yaxis && chart.options.yaxis.labels.formatter) {
      chart.options.yaxis.labels.formatter = eval(chart.options.yaxis.labels.formatter);
    }

    if (chart.options.dataLabels && chart.options.dataLabels.formatter) {
      chart.options.dataLabels.formatter = eval(chart.options.dataLabels.formatter);
    }
  }

  fetch() {
    this.getLineTotals();
    this.getMetricsData();
    this.getApexChart(
      'v1/business-analytics/sales/invoice/last-three-years-month-totals/chart',
      this.queryParameters,
      this.salesInvoiceLinesLastThreeYearsMonthTotalsChart
    );

    const month = new Date().getMonth() + 1;

    this.getApexChart(
      'v1/business-analytics/sales/invoice/year-month-and-months-before-homologous/chart',
      this.queryParameters ? { ...this.queryParameters, month } : { month },
      this.salesInvoiceLinesYearMonthAndMonthsBeforeHomologous
    );

    this.getApexChart('v1/business-analytics/sales/invoice/sales-reps-situation/chart', this.queryParameters, this.salesRepsSituationChart);

    this.getTable(
      'v1/business-analytics/sales/invoice/customers-retentions',
      this.queryParameters ? { ...this.queryParameters, top_n: 30 } : { top_n: 30 },
      this.tableCustomersRetentions
    );
  }

  getMetricsData() {
    const currentYear = new Date().getFullYear();

    // Fetch value by sign and year for Total Billed (sign=1) and Total Credited (sign=-1)
    this.businessAnalyticsService()
      .retrieve('v1/business-analytics/sales/invoice/value-by-sign-and-year', this.queryParameters)
      .then(data => {
        // data is array like: [{year: 2024, total_invoice_amount: 24644995.48, number_of_invoices: 4277, sign: 1}, ...]

        // KPI 1: Total Billed (sign=1 - positive invoices)
        const currentYearBilled = data.find(d => d.year === currentYear && d.sign === 1);
        const previousYearBilled = data.find(d => d.year === currentYear - 1 && d.sign === 1);

        if (currentYearBilled) {
          this.metrics.totalBilled = currentYearBilled.total_invoice_amount || 0;

          if (previousYearBilled && previousYearBilled.total_invoice_amount > 0) {
            const currentValue = currentYearBilled.total_invoice_amount || 0;
            const previousValue = previousYearBilled.total_invoice_amount || 0;
            this.metrics.totalBilledChange = ((currentValue - previousValue) / previousValue) * 100;
          }
        }

        // KPI 2: Total Credited (sign=-1 - credit notes)
        const currentYearCredited = data.find(d => d.year === currentYear && d.sign === -1);
        const previousYearCredited = data.find(d => d.year === currentYear - 1 && d.sign === -1);

        if (currentYearCredited) {
          this.metrics.totalCredited = Math.abs(currentYearCredited.total_invoice_amount || 0);

          if (previousYearCredited && previousYearCredited.total_invoice_amount !== 0) {
            const currentValue = Math.abs(currentYearCredited.total_invoice_amount || 0);
            const previousValue = Math.abs(previousYearCredited.total_invoice_amount || 0);
            if (previousValue > 0) {
              this.metrics.totalCreditedChange = ((currentValue - previousValue) / previousValue) * 100;
            }
          }
        }

        // KPI 4: Annual Growth (Net Revenue Growth Amount)
        // Calculate net revenue (Total Billed - Total Credited)
        // Note: credit note amounts from API are negative, so we add them to get net
        const currentYearNetRevenue = (currentYearBilled?.total_invoice_amount || 0) + (currentYearCredited?.total_invoice_amount || 0);
        const previousYearNetRevenue = (previousYearBilled?.total_invoice_amount || 0) + (previousYearCredited?.total_invoice_amount || 0);

        // Store the growth amount (difference between current and previous year)
        this.metrics.netRevenue = currentYearNetRevenue - previousYearNetRevenue;

        // Calculate YoY percentage
        if (previousYearNetRevenue > 0) {
          this.metrics.annualGrowth = ((currentYearNetRevenue - previousYearNetRevenue) / previousYearNetRevenue) * 100;
        }

        // Annual growth change is the value itself (represents YoY change)
        this.metrics.annualGrowthChange = this.metrics.annualGrowth;
      })
      .catch(err => {
        console.error('Error fetching value by sign and year:', err);
      });

    // KPI 3: Average Invoice Value
    this.businessAnalyticsService()
      .retrieve('v1/business-analytics/sales/invoice/average-value-by-year', this.queryParameters)
      .then(data => {
        // data is array like: [{year: 2024, average_invoice_value: 5762.22, number_of_invoices: 4277, ...}, ...]
        const currentYearData = data.find(d => d.year === currentYear);
        const previousYearData = data.find(d => d.year === currentYear - 1);

        if (currentYearData) {
          this.metrics.averageInvoiceValue = currentYearData.average_invoice_value || 0;

          if (previousYearData && previousYearData.average_invoice_value > 0) {
            const currentAvg = currentYearData.average_invoice_value || 0;
            const previousAvg = previousYearData.average_invoice_value || 0;
            this.metrics.averageInvoiceValueChange = ((currentAvg - previousAvg) / previousAvg) * 100;
          }
        }
      })
      .catch(err => {
        console.error('Error fetching average value by year:', err);
      });
  }

  public retrieveFunctionAuthorization(): Promise<boolean> {
    return new Promise(resolve => {
      this.isFunctionAuthorizationFetching = true;

      this.gardianService()
        .functionAuthorization({ function: ERP_FUNCTION })
        .then(param => {
          this.functionAuthorization.accessTypeObject = param.accessTypeObject;
          this.functionAuthorization.authorizationSite = param.authorizationSite;
          this.functionAuthorization.authorizedAccessTypeObject = param.authorizedAccessTypeObject;
          this.functionAuthorization.defaultSite = param.defaultSite;
          this.functionAuthorization.authorizedCompanies.push(...param.authorizedCompanies);
          this.functionAuthorization.authorizedSites.push(...param.authorizedSites);
          this.functionAuthorization.authorizedSalesRep.push(...param.authorizedSalesRep);
          this.isFunctionAuthorizationFetching = false;
          resolve(true);
        })
        .catch(error => {
          this.isFunctionAuthorizationFetching = false;
          const errMessage = error?.response?.data?.message ?? '';
          this.alertService().showAlert(this, AlertType.Error, `${error.message}${errMessage !== '' ? '<br>' + errMessage : ''}`, null, -1);
          resolve(false);
        });
    });
  }

  getLineTotals() {
    this.businessAnalyticsService()
      .retrieve('v1/business-analytics/sales/invoice/lines/totals', this.queryParameters)
      .then(res => {
        this.slide.data.currencyCode = res['currency_code'];
        this.slide.data.netPriceExcludeTax = this.formatter.toLocaleInteger(res['net_price_exclude_tax']).replace(/[,\.]/g, ' ');
        this.slide.data.number = this.formatter.toLocaleInteger(res['number']).replace(/[,\.]/g, ' ');
      })
      .catch(err => {
        this.slide.data.currencyCode = '';
        this.slide.data.netPriceExcludeTax = 0;
        this.slide.data.number = 0;
        // this.alertService().showHttpError(this, err.response);
      });
  }

  /**
   * Handler function for 'refresh-query'
   * @param payload
   */
  refreshQuery(payload) {
    // Update parent object to reflect parameters selections
    this.parameters = payload;
    this.queryParameters = this.parameterService().toQueryParameters(this.parameters);
    // Updates dashboard data
    this.fetch();
  }
}
